/** @jsxImportSource ./simboluri-jsx */
// «Componente» — vederea Studioului structurii care răspunde la întrebarea
// execuției: DIN CÂTE LUCRURI DIFERITE e făcută structura, de câte ori se
// repetă fiecare, cât costă și cum se cofrează.
//
// Câte un tab pe familie, în ordinea în care se toarnă structura citită de
// sus: elementele verticale (stâlpi și lamele), grinzile, grinzile de
// fundare, ochiurile de placă — apoi ce se fasonează (bare, etrieri) și planul
// de cofrare. Familiile de element sunt clasele de execuție (`clase.py`,
// `componente.py`); fiecare tip se desface în elementele lui, iar fiecare
// element duce la pagina lui. Ținta biroului e cel mult cinci tipuri de
// fiecare fel; ce trece peste se vede pe tab, nu trebuie numărat.
//
// Cifrele vin din devizul spațiului privit (`getEstimare`, aceeași cerere ca
// pagina costurilor): costul unui tip e cel al articolelor lui, repartizat pe
// tip, deci componentele se calculează odată cu devizul, nu separat. Planul
// de cofrare vine din aceeași cerere (`plan_cofrare`).
//
// Aceleași id-uri ca în scena 3D: «tip 3» de aici e «tip 3» din meniul
// «Evidențiază» al layerului realist. Id-urile le SCRIU pașii de armare
// (`tipuri_armare.py`) și rămân ale tipului de la o rulare la alta: lista
// e ordonată pe utilizare, numerotarea nu.

import { Fragment, useEffect, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { bani } from "./bani";
import { getEstimare } from "./apiStructura";
import { hashElement } from "./legaturi";
import Cofraje, { MembriTip } from "./Cofraje";
import MarcaLink from "./MarcaLink";
import { marcaTipului } from "./marci";
import { numar, useT, useTn } from "../i18n";
import type { Cheie, CheiePlural } from "../i18n";
import type { ComponenteDeviz, EstimareStructura, FamilieComponenta, PlanCofrare,
              TipBara, TipElement, TipuriSectiune } from "./tipuri";

const nr = (v: number | null | undefined, zec = 0) =>
  v == null ? "—" : numar(v, { maximumFractionDigits: zec });

/** Tabul vederii: o familie de componente sau planul de cofrare. */
export type TabComponente = FamilieComponenta | "cofrare";

const TABURI: { cheie: TabComponente; nume: Cheie }[] = [
  { cheie: "stalpi", nume: "struct.comp.tab_stalpi" },
  { cheie: "grinzi", nume: "struct.comp.tab_grinzi" },
  { cheie: "fundatie", nume: "struct.comp.tab_fundatie" },
  { cheie: "placa", nume: "struct.comp.tab_placa" },
  { cheie: "bare", nume: "struct.comp.tab_bare" },
  { cheie: "etrieri", nume: "struct.comp.tab_etrieri" },
  { cheie: "cofrare", nume: "struct.comp.tab_cofrare" },
];

const FAMILII: Record<FamilieComponenta, { nume: Cheie; ce: Cheie }> = {
  stalpi: { nume: "struct.comp.fam_stalpi", ce: "struct.comp.fam_stalpi_ce" },
  grinzi: { nume: "struct.comp.fam_grinzi", ce: "struct.comp.fam_grinzi_ce" },
  fundatie: { nume: "struct.comp.fam_fundatie", ce: "struct.comp.fam_fundatie_ce" },
  placa: { nume: "struct.comp.fam_placa", ce: "struct.comp.fam_placa_ce" },
  bare: { nume: "struct.comp.fam_bare", ce: "struct.comp.fam_bare_ce" },
  etrieri: { nume: "struct.comp.fam_etrieri", ce: "struct.comp.fam_etrieri_ce" },
};

const NUMAR_MEMBRI: Partial<Record<FamilieComponenta, CheiePlural>> = {
  fundatie: "struct.comp.axe#n",
  placa: "struct.comp.ochiuri#n",
};

function Antet({ nume, ce, n, tinta, cost }: {
  nume: Cheie; ce: Cheie; n: number; tinta: number; cost: number;
}) {
  const t = useT();
  const tn = useTn();
  const peste = n > tinta;
  return (
    <div className="rc-sect" style={{ display: "flex", alignItems: "baseline",
                                      gap: 10, flexWrap: "wrap" }}>
      <span>{t(nume)}</span>
      <span className={`rc-chip${peste ? " cmp-peste" : ""}`}
            title={t(peste ? "struct.comp.peste_tinta" : "struct.comp.in_tinta",
                     { tinta })}>
        {tn("struct.comp.nr_tipuri#n", n, { tinta })}
      </span>
      <span className="sg-muted" style={{ fontWeight: 400 }}>{bani(cost)}</span>
      <span className="sg-muted" style={{ fontWeight: 400, fontSize: 12 }}>
        {t(ce)}
      </span>
    </div>
  );
}

/** Marca de pe planșă a unui tip: cea scrisă de pas, altfel compusă din
 *  numărul lui (`e3` / `3g`) — v. `marci.ts`. */
export type LaMarca = (marca: string, tip: TipBara) => ReactNode;

function TabelBare({ lista, etrieri, laMarca }: {
  lista: TipBara[]; etrieri: boolean; laMarca?: LaMarca;
}) {
  const tr = useT();
  if (!lista.length) {
    return <div className="sg-muted">{tr("struct.comp.gol_bare")}</div>;
  }
  return (
    <table className="sg-table cmp-tabel">
      <thead>
        <tr>
          <th>{tr("struct.comp.col_tip")}</th>
          <th>{tr("struct.comp.col_ce")}</th>
          <th>{tr("struct.comp.col_utilizari")}</th>
          <th>{tr("struct.comp.col_otel")}</th>
          <th>{tr("struct.comp.col_material")}</th>
          <th>{tr("struct.comp.col_manopera")}</th>
          <th>{tr("struct.comp.col_cost")}</th>
        </tr>
      </thead>
      <tbody>
        {lista.map((t) => (
          <tr key={t.tip}>
            <td className="sg-mono">
              {laMarca ? laMarca(t.marca ?? marcaTipului(t.tip, etrieri), t)
                : (t.marca ?? marcaTipului(t.tip, etrieri))}
            </td>
            <td>{t.descriere}</td>
            <td>
              {tr("struct.comp.buc", { n: nr(t.nr) })}
              <span className="sg-muted">
                {tr("struct.comp.in_elemente", { n: nr(t.elemente) })}
              </span>
            </td>
            <td className="sg-mono">{nr(t.kg)} kg
              <span className="sg-muted">
                {tr("struct.comp.kg_m", { n: nr(t.l_tot_m) })}
              </span></td>
            <td className="sg-mono">{bani(t.material_eur)}</td>
            <td className="sg-mono">{bani(t.manopera_eur)}</td>
            <td className="sg-mono"><strong>{bani(t.cost_eur)}</strong></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Membrii unei clase, ca TABEL cu aceleași coloane ca rândul clasei: fiecare
 *  element cu betonul, cofrajul, oțelul și costul lui, din aceleași cantități
 *  din care e adunat totalul clasei (`componente.clase_cu_costuri`). Un deviz
 *  mai vechi n-are `membri_detaliu`: atunci rămân cipurile cu id-urile. */
function TabelMembri({ c, laElement }: {
  c: TipElement;
  laElement?: (ref: string) => string;
}) {
  const tr = useT();
  const d = c.membri_detaliu;
  if (!d) return <MembriTip membri={c.membri} laElement={laElement} />;
  return (
    <table className="sg-table cmp-tabel cmp-membri-tabel" data-testid="cmp-membri">
      <thead>
        <tr>
          <th>{tr("struct.membri.col_element")}</th>
          <th>{tr("struct.comp.col_ce")}</th>
          <th>{tr("struct.comp.col_beton")}</th>
          <th>{tr("struct.comp.col_cofraj")}</th>
          <th>{tr("struct.comp.col_otel")}</th>
          <th>{tr("struct.comp.col_material")}</th>
          <th>{tr("struct.comp.col_manopera")}</th>
          <th>{tr("struct.comp.col_cost")}</th>
        </tr>
      </thead>
      <tbody>
        {d.map((m) => (
          <tr key={m.element}>
            <td className="sg-mono">
              {laElement ? (
                <Link className="cmp-element" to={laElement(m.element)}>{m.element}</Link>
              ) : m.element}
            </td>
            <td className="sg-muted">{c.descriere}</td>
            <td className="sg-mono">{nr(m.beton_m3, 2)} m³</td>
            <td className="sg-mono">{nr(m.cofraj_m2, 1)} m²</td>
            <td className="sg-mono">{nr(m.otel_kg)} kg</td>
            <td className="sg-mono">{bani(m.material_eur)}</td>
            <td className="sg-mono">{bani(m.manopera_eur)}</td>
            <td className="sg-mono"><strong>{bani(m.cost_eur)}</strong></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Tipurile unei familii de element. Fiecare rând se DESFACE în elementele
 *  lui: chevronul din prima coloană și tot rândul sunt comanda, ca să nu fie
 *  o funcție ascunsă sub un număr subliniat. */
function TabelElemente({ lista, familie, laElement }: {
  lista: TipElement[];
  familie: FamilieComponenta;
  laElement?: (ref: string) => string;
}) {
  const tr = useT();
  const trn = useTn();
  const [deschise, setDeschise] = useState<Set<string>>(() => new Set());
  if (!lista.length) {
    return <div className="sg-muted">{tr("struct.comp.gol_clase")}</div>;
  }
  const comuta = (tip: string) => setDeschise((d) => {
    const n = new Set(d);
    if (n.has(tip)) n.delete(tip); else n.add(tip);
    return n;
  });
  const toate = lista.every((c) => deschise.has(c.tip));
  const cheieNr = NUMAR_MEMBRI[familie] ?? "struct.comp.elemente#n";
  return (
    <>
      <div className="cmp-bara-tabel">
        <span className="sg-muted">{tr("struct.comp.desface_nota")}</span>
        <button type="button" className="btn btn-mic"
                onClick={() => setDeschise(toate ? new Set()
                  : new Set(lista.map((c) => c.tip)))}>
          {tr(toate ? "struct.comp.strange_tot" : "struct.comp.desface_tot")}
        </button>
      </div>
      <table className="sg-table cmp-tabel">
        <thead>
          <tr>
            <th aria-hidden style={{ width: 22 }} />
            <th>{tr("struct.comp.col_clasa")}</th>
            <th>{tr("struct.comp.col_ce")}</th>
            <th>{tr("struct.comp.col_utilizari")}</th>
            <th>{tr("struct.comp.col_beton")}</th>
            <th>{tr("struct.comp.col_cofraj")}</th>
            <th>{tr("struct.comp.col_otel")}</th>
            <th>{tr("struct.comp.col_material")}</th>
            <th>{tr("struct.comp.col_manopera")}</th>
            <th>{tr("struct.comp.col_cost")}</th>
          </tr>
        </thead>
        <tbody>
          {lista.map((c) => {
            const deschis = deschise.has(c.tip);
            return (
              <Fragment key={c.tip}>
                <tr className={`cmp-rand-tip${deschis ? " deschis" : ""}`}
                    onClick={() => comuta(c.tip)}>
                  <td>
                    <button type="button" className="cmp-chevron"
                            aria-expanded={deschis}
                            aria-label={tr("struct.comp.membri_titlu")}
                            title={tr("struct.comp.membri_titlu")}
                            onClick={(e) => { e.stopPropagation(); comuta(c.tip); }}>
                      {deschis ? "▾" : "▸"}
                    </button>
                  </td>
                  <td className="sg-mono">{c.tip}</td>
                  <td>
                    {c.descriere}
                    {c.deschideri_m && (
                      <span className="sg-muted">
                        {tr("struct.comp.deschideri", {
                          min: nr(c.deschideri_m[0], 2),
                          max: nr(c.deschideri_m[1], 2),
                        })}
                      </span>
                    )}
                  </td>
                  <td className="cmp-membri">{trn(cheieNr, c.nr)}</td>
                  <td className="sg-mono">{nr(c.beton_m3, 1)} m³</td>
                  <td className="sg-mono">{nr(c.cofraj_m2)} m²</td>
                  <td className="sg-mono">{nr(c.otel_kg)} kg</td>
                  <td className="sg-mono">{bani(c.material_eur)}</td>
                  <td className="sg-mono">{bani(c.manopera_eur)}</td>
                  <td className="sg-mono"><strong>{bani(c.cost_eur)}</strong></td>
                </tr>
                {deschis && (
                  <tr className="cmp-rand-membri">
                    <td />
                    <td colSpan={9}>
                      <TabelMembri c={c} laElement={laElement} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

/** TIPURILE DE SECȚIUNE ale elementelor verticale: cofrajul b × L al fiecărei
 *  piese, pe fiecare nivel, cu stâlpul de 30 × 30 cu tot — numărul pe care
 *  biroul îl judecă la geometrie. Vine din Pasul 4, deci se vede și pe un
 *  rând fără armare; clasele de execuție de mai jos îl desfac pe înălțimi
 *  și pe armare. Elementul duce la pagina primei lui piese. */
export function TipuriSectiuneBloc({ ts, laElement }: {
  ts: TipuriSectiune;
  laElement?: (ref: string) => string;
}) {
  const t = useT();
  const tn = useTn();
  const peste = ts.nr > ts.tinta;
  const deCe = ts.tipuri.some((x) => ts.blocate?.[x.tip]);
  const laPiesa = laElement ? (ref: string) => laElement(`${ref}.1`) : undefined;
  return (
    <section className="cmp-familie" data-testid="cmp-sectiuni">
      <div className="rc-sect" style={{ display: "flex", alignItems: "baseline",
                                        gap: 10, flexWrap: "wrap" }}>
        <span>{t("struct.comp.sectiuni_titlu")}</span>
        <span className={`rc-chip${peste ? " cmp-peste" : ""}`}
              title={t(peste ? "struct.comp.sectiuni_peste" : "struct.comp.sectiuni_in",
                       { tinta: ts.tinta, exceptie: ts.exceptie })}>
          {tn("struct.comp.sectiuni_nr#n", ts.nr, { tinta: ts.tinta })}
        </span>
      </div>
      <div className="sg-muted" style={{ fontSize: 12, margin: "4px 0 8px", maxWidth: 760 }}>
        {t("struct.comp.sectiuni_ce", { tinta: ts.tinta, exceptie: ts.exceptie })}
      </div>
      <table className="sg-table cmp-tabel">
        <thead>
          <tr>
            <th>{t("struct.comp.col_sectiune")}</th>
            <th title={t("struct.comp.piese_titlu")}>{t("struct.comp.col_piese")}</th>
            <th>{t("struct.comp.col_elemente")}</th>
            {deCe && <th>{t("struct.comp.col_de_ce")}</th>}
          </tr>
        </thead>
        <tbody>
          {ts.tipuri.map((x) => (
            <tr key={x.tip}>
              <td className="sg-mono">{x.tip}</td>
              <td>{tn("struct.comp.piese#n", x.piese)}</td>
              <td><MembriTip membri={x.elemente} laElement={laPiesa} /></td>
              {deCe && <td className="sg-muted">{ts.blocate?.[x.tip] ?? ""}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** Are devizul clase de execuție de arătat? Pe un rând fără armare toate
 *  familiile de element și de bară sunt goale. */
function cuClase(comp: ComponenteDeviz): boolean {
  return Object.values(comp.rezumat ?? {}).some((r) => (r?.nr_tipuri ?? 0) > 0);
}

/** O familie de componente: antetul cu numărul de tipuri și tabelul ei. */
function Familie({ comp, familie, laElement, laMarca }: {
  comp: ComponenteDeviz;
  familie: FamilieComponenta;
  laElement?: (ref: string) => string;
  laMarca?: LaMarca;
}) {
  const t = useT();
  const f = FAMILII[familie];
  const lista = (comp[familie] ?? []) as (TipBara | TipElement)[];
  const rez = comp.rezumat?.[familie];
  const bare = familie === "bare" || familie === "etrieri";
  return (
    <section className="cmp-familie" data-testid={`cmp-${familie}`}>
      <Antet nume={f.nume} ce={f.ce} n={rez?.nr_tipuri ?? lista.length}
             tinta={comp.tinta} cost={rez?.cost_eur ?? 0} />
      {bare && comp.fara_tip && comp.fara_tip.pozitii > 0 && (
        <div className="sg-muted" style={{ marginBottom: 8 }}
             data-testid="cmp-fara-tip">
          {t("struct.comp.fara_tip", {
            pozitii: nr(comp.fara_tip.pozitii),
            kg: nr(comp.fara_tip.kg),
            elemente: nr(comp.fara_tip.elemente),
          })}
        </div>
      )}
      {bare ? (
        <TabelBare lista={lista as TipBara[]} etrieri={familie === "etrieri"}
                   laMarca={laMarca} />
      ) : (
        <TabelElemente lista={lista as TipElement[]} familie={familie}
                       laElement={laElement} />
      )}
    </section>
  );
}

/** Câte tipuri are tabul (`null` = nimic de arătat: tabul e gri). */
function nrTab(tab: TabComponente, comp: ComponenteDeviz,
               plan: PlanCofrare | null | undefined): number | null {
  if (tab === "cofrare") return plan ? plan.nr_tipuri : null;
  const lista = comp[tab] as unknown[] | undefined;
  return lista?.length ? lista.length : null;
}

export default function Componente({ comp, plan, laElement, laMarca }: {
  comp: ComponenteDeviz;
  plan?: PlanCofrare | null;
  laElement?: (ref: string) => string;
  /** cum se scrie marca unui tip de bară / etrier (linkul spre pagina ei) */
  laMarca?: LaMarca;
}) {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const numere = Object.fromEntries(
    TABURI.map((x) => [x.cheie, nrTab(x.cheie, comp, plan)])) as
    Record<TabComponente, number | null>;
  // tabul stă în URL (`?comp=`), ca un link să deschidă exact familia; fără
  // el, primul tab care are ce arăta
  const cerut = TABURI.find((x) => x.cheie === params.get("comp"))?.cheie;
  const tab: TabComponente = cerut
    ?? TABURI.find((x) => numere[x.cheie] != null)?.cheie ?? "stalpi";
  const alege = (k: TabComponente) => {
    const q = new URLSearchParams(params);
    q.set("comp", k);
    setParams(q, { replace: true });
  };
  return (
    <div style={{ marginTop: 10 }} data-testid="componente">
      <div className="sg-muted" style={{ marginBottom: 10, maxWidth: 760 }}>
        {t("struct.comp.intro")}
      </div>
      <div className="pf-tabs cmp-taburi" role="tablist">
        {TABURI.map((x) => {
          const n = numere[x.cheie];
          const gol = n == null;
          // ținta e pe familie; planul de cofrare le numără pe toate cinci
          // laolaltă, deci își spune depășirea pe fiecare familie, înăuntru
          const peste = n != null && x.cheie !== "cofrare" && n > comp.tinta;
          return (
            <button key={x.cheie} type="button" role="tab"
                    aria-selected={tab === x.cheie}
                    className={`pf-tab${tab === x.cheie ? " active" : ""}${gol ? " gol" : ""}`}
                    title={gol ? t("struct.comp.tab_gol") : undefined}
                    onClick={() => alege(x.cheie)}>
              {t(x.nume)}
              {!gol && (
                <span className={`cmp-tab-nr${peste ? " peste" : ""}`}>{n}</span>
              )}
            </button>
          );
        })}
      </div>
      {tab === "cofrare" ? (
        plan ? <Cofraje plan={plan} laElement={laElement} />
          : <div className="sg-muted">{t("struct.ret.planul_cofrare_apare")}</div>
      ) : (
        <Familie comp={comp} familie={tab} laElement={laElement} laMarca={laMarca} />
      )}
    </div>
  );
}

/** Vederea «Componente» a Studioului: componentele spațiului privit (corpul
 *  activ, altfel rândul), din devizul lui. */
export function VedereComponente({ projectId, cloneId, solutie }: {
  projectId: string;
  /** spațiul din care se citește devizul (`useStructure().cloneId`) */
  cloneId: string | null;
  /** referința de legătură a rândului privit: din ea se fac linkurile
   *  spre paginile elementelor (`legaturi.hashElement`) */
  solutie?: string | null;
}) {
  const t = useT();
  // `undefined` = cererea e pe drum; `null` = spațiul n-are deviz de dat
  const [estimare, setEstimare] = useState<EstimareStructura | null>();
  useEffect(() => {
    // fără rând privit nu se citește nimic: spațiul «proiect» n-are pași
    if (cloneId == null) {
      setEstimare(null);
      return;
    }
    let renunta = false;
    setEstimare(undefined);
    getEstimare(projectId, cloneId)
      .then((e) => { if (!renunta) setEstimare(e); })
      .catch(() => { if (!renunta) setEstimare(null); });
    return () => { renunta = true; };
  }, [projectId, cloneId]);
  const laElement = solutie !== undefined
    ? (ref: string) => hashElement(projectId, ref, solutie) : undefined;
  const laMarca: LaMarca | undefined = solutie
    ? (marca, tip) => (
      <MarcaLink marca={marca} projectId={projectId} solutie={solutie}
                 cloneId={cloneId} descriere={tip.descriere} />
    ) : undefined;
  const ts = estimare?.tipuri_sectiune;
  return (
    <div className="pj-step-page">
      <h2>{t("struct.comp.titlu")}</h2>
      {estimare === undefined ? (
        <div className="sg-muted">{t("comun.se_incarca")}</div>
      ) : (
        <>
          {ts && <TipuriSectiuneBloc ts={ts} laElement={laElement} />}
          {estimare?.componente && cuClase(estimare.componente) ? (
            <Componente comp={estimare.componente} plan={estimare.plan_cofrare}
                        laElement={laElement} laMarca={laMarca} />
          ) : (
            <div className="sg-muted" style={{ marginTop: 10 }}>
              {t(ts ? "struct.comp.sectiuni_fara_armare"
                    : "struct.ret.componentele_apar_odata")}
            </div>
          )}
        </>
      )}
    </div>
  );
}
